import {
  ITableRow,
  ITableViewWithColumns,
  ITableViewFilter,
  ITableViewColour,
  ITableViewRowLock,
  ITableViewGroup,
  ITableViewSort,
  ITableColumn,
  ITableViewColumn,
  ICommentThreadStats,
  ITag,
  EditorContent,
  ICellValue,
  ITableJoin,
  ITable,
  IContextProperties,
  IContextMenuState,
  ITableViewChart,
  ICalendarSettings,
  IDuplicatedChild
} from 'types'
import { CompressedColumnStore } from 'components/spreadsheet/helpers/compressed-column-store'
import { ITableViewHistory } from './contexts/data/actions'

export interface IViewCoordinate {
  originalRowId: string | null
  originalColumnId: string | null
}

/** Where a synthetic (transposed / matrix) cell writes to, looked up by `${rowId}:${columnId}`.
 *  A Map works; the transpose view derives the answer from the ids instead of storing one entry
 *  per cell, which on a large table was the biggest allocation of the whole view. */
export interface IViewCoordinateMap {
  get(key: string): IViewCoordinate | undefined
  has(key: string): boolean
  readonly size: number
}

/** A derived view that shows only part of the data because the whole would not fit in memory.
 *  `message` is what the grid tells the user. */
export interface IViewTruncation {
  shown: number
  total: number
  message: string
}

export interface ITransposedResult {
  rows: ITableRow[]
  columns: ITableViewColumn[]
  columnIdToIndex: Map<string, number>
  indexToColumnId: string[]
  coordinateMap: IViewCoordinateMap
  truncations: IViewTruncation[]
}

/** Generic view context for any transformed view (transpose, matrix, etc.).
 *  Provides access to the original pre-transformation data. */
export interface IViewContext {
  originalColumns: ITableViewColumn[]
  originalRows: ITableRow[]
  originalColumnIdToIndex: Map<string, number>
  originalColumnStore: CompressedColumnStore | null
  coordinateMap: IViewCoordinateMap
  // Why the view shows only part of the data, one line each; empty when it shows all of it.
  truncations?: IViewTruncation[]
  // Synthetic cell keys (``${rowId}:${columnId}``) that aggregated more than
  // one source row in a matrix view — rendered with the spreadsheet's
  // default validation indicator so the user can see the problem cells.
  matrixCollisionCells?: Set<string>
  // Synthetic cell keys for matrix data cells with no backing source row.
  // The Row component locks these so users can't type into a cell whose
  // writes would be silently dropped by ``mapMatrixCells``.
  matrixSparseCells?: Set<string>
  // Synthetic column IDs that are structurally read-only in a transformed view,
  // independent of row-lock rules: matrix row-label identifiers, matrix data
  // columns over a formula/script/joined/synced source, the transpose "field"
  // column, etc. The Row component locks these so they don't render as editable,
  // and the cut/copy guards treat them as read-only.
  readOnlyColumns?: Set<string>
}

export interface ITableDetails {
  publicId: string
  name: string
  type: string | null
  permissionLevel: number
  logo: string | null
  permissions: string[][]
  columns: ITableColumn[]
  defaultViewId: string
  joins: ITableJoin[]
  keepValidationsInSync: boolean
  keepColoursInSync: boolean
  isDeleted: boolean
  deletedAt: string | null
  isSynced: boolean
  syncProvider: string | null
  syncType: string | null
  isSyncing: boolean
  duplicatedChildren: IDuplicatedChild[]
  lastSync?: string
  syncHourlyFrequency: number
  allowComments: boolean
  projectPublicId: string
  failedSyncAttempts: number
  syncProjectIds?: string[]
  syncViewIds?: string[]
  syncAllHubs?: boolean
}

export interface IUserConfiguration {
  filterSettings: ITableViewFilter[]
  groupSettings: ITableViewGroup[]
  colourSettings: ITableViewColour[]
  hiddenColumns: string[]
  sortSettings: ITableViewSort[]
  chartSettings: ITableViewChart | null
  calendarSettings: ICalendarSettings | null
  rowLockSettings: ITableViewRowLock[]
  unsavedChanges: boolean
}

export interface ISpreadsheetData {
  tableDetails: ITableDetails
  viewDetails: ITableViewWithColumns
  userConfiguration: IUserConfiguration
  processId?: string
  processSectionId?: string
  tableView: ITableViewWithColumns[]
  activeTableView: string
  comments: ICommentThreadStats[]
  rows: ITableRow[]
  visibleBitmap: Uint8Array | null // null = no filtering active (all visible), 1 = visible, 0 = filtered out
  visibleCount: number // cached count of visible rows (bitmap popcount)
  totalRows: number // the total rows we've downloaded so far, this starts incrementing when we stream in rows
  totalRowsOnServer: number // fetched when we get the table/view
  loading: boolean
  loadingProgress: number // 0-100 percentage of rows streamed
  rowStreamComplete: boolean
  // Changes made while a progressive stream is still running to rows that had not arrived yet: the
  // ids of deleted rows, and the latest version of updated ones. The server's cursor predates the
  // change and still sends each row as it was, so the change is applied to the row when its batch
  // lands. Null once the stream is complete.
  streamPendingDeletes: Set<string> | null
  streamPendingUpdates: Map<string, ITableRow> | null
  refreshing: boolean
  // Short contextual label shown in the busy overlay while `refreshing` (e.g. "Adding row…").
  // Null when not busy or when no label was supplied.
  busyLabel: string | null
  error: boolean
  errorMessage: string
  columnWidths: {
    frozenWidth: number
    unfrozenWidth: number
  }
  isAdmin: boolean
  isContributor: boolean
  collapsedGroupIds: Set<string>
  collapsedColumnIds: Set<string>
  originalViewColumns: string[]
  /** Original (pre-transformation) data stashed when the view uses synthetic columns.
   *  Null when the view is a standard spreadsheet with no transformation. */
  viewContext: IViewContext | null
  processTags: ITag[]
  processVariables: string[]
  processVariableValues: string[]
  searchTerm: string
  searchFound: number
  selectedSearchCell: { rowId: string | null; columnId: string | null; rowNumber: number; columnNumber: number }
  columnValuesCount: IColumnValuesCount
  columnIdToIndex: Map<string, number> // Maps columnId to array index for rowData lookups
  indexToColumnId: string[] // Maps array index back to columnId
  columnStore: CompressedColumnStore | null
  dataVersion: number
  searchedRows: Map<string, string[]> // Maps rowId to array of columnIds where search matched
  isPollingEnabled: boolean
  history: ITableViewHistory[]
  historyPoint: number
  errorStatusCode?: number
  templateRender?: boolean
}

export enum IFilterType {
  contains = 'contains',
  not_contains = 'not_contains',
  starts_with = 'starts_with',
  ends_with = 'ends_with',
  eq = 'eq',
  neq = 'neq',
  in = 'in',
  gte = 'gte',
  lte = 'lte',
  gt = 'gt',
  lt = 'lt',
  one_of = 'one_of',
  not_one_of = 'not_one_of',
  is_null = 'is_null',
  is_not_null = 'is_not_null',
  is_valid = 'is_valid',
  is_not_valid = 'is_not_valid',
  all_values = 'all_values'
}

export enum IAggregateTypes {
  'None' = 0,
  '# Rows' = 1,
  'Empty' = 2,
  'Filled' = 3,
  'Unique' = 4,
  '% Total Rows' = 16,
  '% Empty' = 5,
  '% Filled' = 6,
  '% Unique' = 7,
  'Sum' = 8,
  'Average' = 9,
  'Min' = 10,
  'Max' = 11,
  'Range' = 12,
  'Earliest Date' = 13,
  'Latest Date' = 14,
  'Date Range (Days)' = 15,
  '# Issues' = 17,
  '# Valid' = 18,
  '% Issues' = 19,
  '% Valid' = 20
}

export enum sortDirections {
  asc = 'asc',
  desc = 'desc'
}

export enum rowHeights {
  'Small' = 0,
  'Medium' = 1,
  'Tall' = 2,
  'Extra Tall' = 3
}

export type IColumnTypes =
  | 'text'
  | 'integer'
  | 'float'
  | 'percentage'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'link'
  | 'multilink'
  | 'tag'
  | 'variable'
  | 'attachment'
  | 'phone'
  | 'email'
  | 'vote'
  | 'checkbox'
  | 'duration'
  | 'json'

export interface TableSortObj {
  columnId: string
  columnName: string
  direction: sortDirections
}

export type TableSort = TableSortObj[]

export interface TableGroupObj {
  columnId: string
  columnName: string
  direction: sortDirections
}

export type TableGroup = TableGroupObj[]

export type TableHidden = string[]

export type SpreadsheetReducerActions =
  | { type: 'CHANGE_COLUMN_WIDTH'; columnId: string; columnWidth: number }
  | { type: 'CHANGE_TRANSPOSE_COLUMN_WIDTH'; columnId: string; width: number }
  | { type: 'CHANGE_MATRIX_COLUMN_WIDTH'; columnId: string; width: number }
  | { type: 'CHANGE_COLUMN_AGGREGATE'; columnId: string; aggregate: number }
  | { type: 'SET_SPREADSHEET_DATA'; spreadsheetData: ISpreadsheetData }
  | {
      type: 'SET_SPREADSHEET_ROWS'
      rows: ITableRow[]
      totalRows: number
      visibleBitmap?: Uint8Array | null
      visibleCount?: number
    }
  | { type: 'ADD_COLUMN'; column: ITableViewColumn }
  | { type: 'UPDATE_COLUMN'; column: ITableViewColumn }
  | { type: 'DELETE_COLUMN'; columnId: string }
  | { type: 'DELETE_COLUMNS'; columnIds: string[] }
  | { type: 'HIDE_COLUMN'; columnId: string }
  | { type: 'SHOW_COLUMN'; columnId: string }
  | { type: 'REQUIRE_COLUMN'; columnId: string }
  | { type: 'UNREQUIRE_COLUMN'; columnId: string }
  | { type: 'HIDE_ALL_COLUMNS' }
  | { type: 'SHOW_ALL_COLUMNS' }
  | { type: 'REQUIRE_ALL_COLUMNS' }
  | { type: 'UNREQUIRE_ALL_COLUMNS' }
  | { type: 'REFRESH'; refreshing: boolean; label?: string }
  | { type: 'LOADING'; loading: boolean }
  | { type: 'SET_LOADING_PROGRESS'; loadingProgress: number }
  // Progressive row streaming: show the grid from the first batch, keep appending, then finish
  // in place (see canRenderRowsProgressively).
  | {
      type: 'START_ROW_STREAM_RENDER'
      spreadsheetData: ISpreadsheetData
      columnValuesCount: IColumnValuesCount
      loadingProgress: number
    }
  // `rows` and `columnValuesCount` on these two are what arrived since the previous dispatch (the
  // reducer appends and merges them), not the whole table.
  | {
      type: 'STREAM_ROWS_PROGRESS'
      rows: ITableRow[]
      columnValuesCount: IColumnValuesCount
      loadingProgress: number
    }
  | { type: 'FINISH_ROW_STREAM'; rows: ITableRow[]; columnValuesCount: IColumnValuesCount }
  | { type: 'CHANGE_COLUMN_ORDER'; columnId: string; newIndex: number }
  | { type: 'CHANGE_ALL_COLUMNS_ORDER'; columns: ITableViewColumn[] }
  | { type: 'CHANGE_ROW_ORDER'; rowId: string; newIndex: number; newSortOrder: number }
  | {
      type: 'EDIT_CELL'
      columnId: string
      rowId: string
      value: { [key: string]: ICellValue }
      oldValue: ICellValue
    }
  | { type: 'CHANGE_ROW_HEIGHT'; newRowHeight: number }
  | { type: 'ADD_SORT'; columnId: string; columnName: string }
  | { type: 'DELETE_SORT'; columnId: string }
  | { type: 'CHANGE_SORT_DIRECTION'; columnId: string; newDirection: sortDirections }
  | { type: 'CHANGE_SORT_ORDER'; existingIndex: number; newIndex: number }
  | { type: 'CHANGE_FROZEN_INDEX'; frozenIndex: number }
  | { type: 'CHANGE_DISPLAY_VALIDATION_ROWS'; displayValidationErrorRows: number }
  | { type: 'CHANGE_DISPLAY_COMMENT_ROWS'; displayCommentRows: number }
  | { type: 'CHANGE_COLLAPSED_GROUP_VIEW'; collapsedGroupView: boolean }
  | { type: 'CHANGE_UNPACK_MULTISELECT_GROUP_VIEW'; unpackMultiselectGroupView: boolean }
  | { type: 'CHANGE_TREE_GROUP_VIEW'; treeGroupView: boolean }
  | { type: 'SET_PIVOT_COLUMN'; pivotColumnId: string | null }
  | { type: 'SET_MATRIX_SETTINGS'; matrixSettings: import('types').IMatrixSettings }
  | { type: 'ADD_VIEW'; view: ITableViewWithColumns; inProcess: boolean }
  | { type: 'SET_VIEW'; viewId: string; viewDetails?: ITableViewWithColumns; inProcess?: boolean }
  | { type: 'HYDRATE_VIEW'; viewDetails: ITableViewWithColumns }
  | { type: 'ADD_HISTORY'; event: ITableViewHistory }
  | { type: 'UPDATE_HISTORY_POINT'; point: number }
  | {
      type: 'UPDATE_VIEW'
      field:
        | 'name'
        | 'description'
        | 'type'
        | 'disableNewRow'
        | 'disableSyncCsv'
        | 'allowContributorDelete'
        | 'displayValidationErrorRows'
        | 'displayCommentRows'
        | 'collapsedGroupView'
        | 'unpackMultiselectGroupView'
      viewId: string
      value: string | boolean | EditorContent | number | null
    }
  | {
      type: 'UPDATE_TABLE'
      field:
        | 'name'
        | 'logo'
        | 'keepValidationsInSync'
        | 'keepColoursInSync'
        | 'isDeleted'
        | 'deletedAt'
        | 'syncHourlyFrequency'
        | 'isSyncing'
        | 'failedSyncAttempts'
        | 'lastSync'
        | 'allowComments'
      value: string | string[] | boolean | number | null
    }
  | { type: 'UPDATE_TABLE_JOINS'; table: ITable }
  | { type: 'DELETE_VIEW'; viewId: string }
  | { type: 'SAVE_CURRENT_VIEW' }
  | { type: 'TOGGLE_GROUP_COLLAPSE'; groupId: string }
  | { type: 'ADD_GROUP'; columnId: string; columnName: string }
  | { type: 'ADD_CHART'; chart: ITableViewChart }
  | { type: 'SET_CUSTOM_VIEW'; viewId: string; customView: import('types').ICustomView | null }
  | { type: 'SET_CUSTOM_VIEW_LATEST'; viewId: string; latest: import('types').ICustomViewVersion | null }
  | { type: 'SET_CALENDAR_SETTINGS'; calendarSettings: ICalendarSettings | null }
  | { type: 'DELETE_GROUP'; columnId: string }
  | { type: 'CHANGE_GROUP_DIRECTION'; columnId: string; newDirection: sortDirections }
  | { type: 'CHANGE_GROUP_ORDER'; existingIndex: number; newIndex: number }
  | { type: 'CHANGE_GROUP_COLUMN'; index: number; columnId: string; columnName: string }
  | { type: 'EXPAND_ALL_GROUPS' }
  | { type: 'COLLAPSE_ALL_GROUPS'; groupIds: string[] }
  // `viewId`: the view the change was made in. A panel saves a moment after the author stops typing,
  // and a change that lands after they've switched view is dropped rather than applied to the new one.
  | { type: 'UPDATE_FILTERS'; filters: ITableViewFilter[]; viewId?: string }
  | { type: 'UPDATE_COLOURS'; colours: ITableViewColour[]; viewId?: string }
  | { type: 'UPDATE_LOCKS'; locks: ITableViewRowLock[]; viewId?: string }
  | { type: 'APPEND_SPREADSHEET_ROWS'; rows: ITableRow[] }
  | { type: 'REFRESH_SPREADSHEET_ROWS'; rows: ITableRow[]; totalRows: number }
  | { type: 'UPDATE_ROWS'; rows: ITableRow[] }
  | { type: 'INSERT_ROW'; row: ITableRow; nextRowPublicId?: string }
  | { type: 'APPEND_ROWS'; rows: Array<ITableRow> }
  | { type: 'DELETE_ROWS'; rowIds: string[] }
  | { type: 'COMMENT_THREADS_CHANGE'; rowId: string; noOpenCommentThreads: number; noResolvedCommentThreads: number }
  | { type: 'TOGGLE_COLUMN_COLLAPSE'; columnId: string; rows: ITableRow[] }
  | { type: 'SEARCH_TABLE'; searchTerm: string }
  | { type: 'CLEAR_SEARCH' }
  | { type: 'SET_SELECTED_SEARCH_CELL'; rowId: string; columnId: string; columnNumber: number; rowNumber: number }
  | { type: 'SET_COLUMN_VALUES_COUNT'; columnValuesCount: IColumnValuesCount }
  | {
      type: 'FINISH_LOADING'
      spreadsheetData: ISpreadsheetData
      columnValuesCount: IColumnValuesCount
    }
  | { type: 'BULK_EDIT_CELL'; cells: ICellUpdateAction[] }
  | { type: 'REFRESH_SELECT_OPTIONS'; view: ITableViewWithColumns }
  | { type: 'RESET'; initialState: ISpreadsheetData }
  | { type: 'DELETE_TABLE' }

export interface IColumnValuesCount {
  [columnId: string]: { [rowValue: string]: number }
}

export interface ISpreadsheetContextMenuState extends IContextMenuState {
  columnNumber: number
  columnId: string
  rowNumber: number
  rowId: string
}

export type GroupByDictionary = Record<
  string,
  {
    childRows: ITableRow[]
    childGroups: GroupByDictionary | ITableRow[]
    startRowIndex: number
    groupByColumn: ITableColumn
  }
>

// Wrapper when row is part of a group
export interface ITableRowInGroup extends ITableRow {
  start: boolean
  isGrouped: boolean
  groupsToRender: string[]
  groupKeys: string[]
}

export interface IGroupCollapsed {
  isCollapsedRow: boolean
  row: ITableRow
  groupKey: string
  groupKeys: string[]
  groupsToRender: string[]
}

// Tree group row (used in tree grouping mode)
export interface ITreeGroupRow {
  isTreeGroupRow: true
  depth: number
  label: string
  groupKey: string
  groupKeys: string[]
  isExpanded: boolean
  childRows: ITableRow[]
  groupByColumn: ITableColumn
}

// For addrow within a group
export interface IGroupRowCreate {
  isAddRow: boolean
  lastGroupRow: ITableRow
}

export interface IViewPortColumn {
  column: ITableViewColumn
  position: number
  isGrouped: boolean
  isFrozen: boolean
  isLastFrozenColumn: boolean
  left: number
  originalPosition: number
  isLastGroupedColumn: boolean
}

export interface ISelectedCell {
  currentRowId: string
  currentColumnId: string
  nextRowId: string
  nextColumnId: string
  prevRowId: string
  prevColumnId: string
  startRowId: string
  finalRowId: string
  finalRowNumber: number
  startColumnId: string
  finalColumnId: string
  finalColumnNumber: number
}

export interface IUIFilter {
  cid: string
  filter: ITableViewFilter
}

export interface IResultFieldValidation {
  pass: boolean
  error?: string
}

export interface ISelectOption {
  value: string
  label: string
}

export interface SpreadsheetInProcessContext {
  processId: string
  processSectionId: string
}

export interface IEditCompleteOptions {
  moveToNextCell?: boolean
  saveValue?: boolean
}

export interface ISearchCell {
  columnId: string
  rowId: string
}

export interface ICellUpdateAction {
  columnId: string
  rowId: string
  value: Record<string, ICellValue>
}

export interface ISelectedCellState {
  rowId: string
  columnId: string
  editing: boolean
  value: ICellValue
  rowNumber: number
  columnNumber: number
}

// Grouped columns has no cells of its own so needs to be separately tracked
export interface ISelectedGroupCell {
  columnId: string
  groupKey: string
  rowId: string
}

export interface ISelectedCellRange {
  endRowIndex: number
  endColumnIndex: number
  endColumnId?: string
}

export interface ISelectedSearchCell {
  rowId: string | null
  columnId: string | null
  rowNumber: number
  columnNumber: number
}

export interface ITableViewHistoryCell extends Omit<IContextProperties, 'projectId'> {
  columnId: string
  rowId: string
  oldValue: ICellValue
  newValue: ICellValue
}

export interface IEditColumn {
  columnId: string | undefined
  columnNumber: number | undefined
}

export interface IDownloadZipProgress {
  done: number
  total: number
}

export type FillDirection = 'down' | 'up' | 'left' | 'right'

export interface IFillDragState {
  // Source range (the selected cells providing values/pattern)
  sourceStartRowIndex: number
  sourceEndRowIndex: number
  sourceStartColId: string
  sourceEndColId: string
  // Current drag target endpoint
  targetRowIndex: number
  targetColId: string
  // Lock direction after first significant mouse movement
  direction: FillDirection | null
}
